import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import api from '../../api/axios';
import { Client, Server } from '../../types';
import { useTranslation } from 'react-i18next';

interface ClientForm {
  name: string;
  email: string;
  password: string;
  phone: string;
  companyName: string;
  serverId: string;
  maxUsers: string;
  subscriptionEnd: string;
}

const initialForm: ClientForm = {
  name: '',
  email: '',
  password: '',
  phone: '',
  companyName: '',
  serverId: '',
  maxUsers: '10',
  subscriptionEnd: '',
};

const Clients: React.FC = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState<Client[]>([]);
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [form, setForm] = useState<ClientForm>(initialForm);
  const [formErrors, setFormErrors] = useState<Partial<ClientForm>>({});
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchClients();
    fetchServers();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get<Client[]>('/admin/clients');
      setClients(response.data);
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t('admin.clients.fetchFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchServers = async () => {
    try {
      const response = await api.get<Server[]>('/admin/servers');
      setServers(response.data);
    } catch (err: any) {
      console.error('Failed to fetch servers:', err);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const validateForm = (isEdit: boolean): boolean => {
    const errors: Partial<ClientForm> = {};
    if (!form.name.trim()) errors.name = t('admin.clients.nameRequired');
    if (!form.email.trim()) errors.email = t('admin.clients.emailRequired');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && form.email.trim()) {
      errors.email = t('admin.clients.emailInvalid');
    }
    if (!isEdit && !form.password.trim()) errors.password = t('admin.clients.passwordRequired');
    if (form.password.trim() && form.password.length < 6) {
      errors.password = t('admin.clients.passwordMin');
    }
    if (!form.phone.trim()) errors.phone = t('admin.clients.phoneRequired');
    if (!form.companyName.trim()) errors.companyName = t('admin.clients.companyRequired');
    if (!form.serverId) errors.serverId = t('admin.clients.serverRequired');
    if (!form.maxUsers.trim()) errors.maxUsers = t('admin.clients.maxUsersRequired');
    if (isNaN(Number(form.maxUsers)) || Number(form.maxUsers) < 1) {
      errors.maxUsers = t('admin.clients.maxUsersInvalid');
    }
    if (!form.subscriptionEnd) errors.subscriptionEnd = t('admin.clients.subscriptionRequired');

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openCreateDialog = () => {
    setEditingClient(null);
    setForm(initialForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (client: Client) => {
    setEditingClient(client);
    setForm({
      name: client.name,
      email: client.email,
      password: '',
      phone: client.phone || '',
      companyName: client.companyName || '',
      serverId: String(client.serverId),
      maxUsers: String(client.maxUsers),
      subscriptionEnd: client.subscriptionEnd ? client.subscriptionEnd.substring(0, 10) : '',
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    const isEdit = !!editingClient;
    if (!validateForm(isEdit)) return;

    setSubmitting(true);
    try {
      const payload: any = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        companyName: form.companyName,
        serverId: parseInt(form.serverId),
        maxUsers: parseInt(form.maxUsers),
        subscriptionEnd: form.subscriptionEnd,
      };

      if (form.password.trim()) {
        payload.password = form.password;
      }

      if (isEdit) {
        await api.put(`/admin/clients/${editingClient!.id}`, payload);
        showSnackbar(t('admin.clients.updatedSuccess'), 'success');
      } else {
        payload.password = form.password;
        await api.post('/admin/clients', payload);
        showSnackbar(t('admin.clients.createdSuccess'), 'success');
      }

      setDialogOpen(false);
      setForm(initialForm);
      setEditingClient(null);
      setFormErrors({});
      fetchClients();
    } catch (err: any) {
      const msg = isEdit ? t('admin.clients.updateFailed') : t('admin.clients.createFailed');
      showSnackbar(err.response?.data?.message || msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (id: number, currentActive: boolean) => {
    try {
      await api.patch(`/admin/clients/${id}/toggle-status`);
      showSnackbar(
        currentActive ? t('admin.clients.deactivatedSuccess') : t('admin.clients.activatedSuccess'),
        'success'
      );
      fetchClients();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t('admin.clients.toggleFailed'), 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('admin.clients.deleteConfirm'))) return;

    try {
      await api.delete(`/admin/clients/${id}`);
      showSnackbar(t('admin.clients.deletedSuccess'), 'success');
      fetchClients();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t('admin.clients.deleteFailed'), 'error');
    }
  };

  const handleInputChange = (field: keyof ClientForm) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setForm({ ...form, [field]: e.target.value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: undefined });
    }
  };

  const handleSelectChange = (field: keyof ClientForm) => (
    e: SelectChangeEvent<string>
  ) => {
    setForm({ ...form, [field]: e.target.value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: undefined });
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#1a237e' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', mb: 0.5 }}>
            {t('admin.clients.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#78909c' }}>
            {t('admin.clients.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('common.refresh')}>
            <IconButton onClick={fetchClients} sx={{ color: '#1a237e' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
              boxShadow: '0 4px 15px rgba(26,35,126,0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #283593 0%, #1565c0 100%)',
              },
            }}
          >
            {t('admin.clients.addClient')}
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: '#f8f9fa',
                '& th': {
                  fontWeight: 700,
                  color: '#37474f',
                  fontSize: '0.85rem',
                  borderBottom: '2px solid #e0e0e0',
                },
              }}
            >
              <TableCell>{t('common.name')}</TableCell>
              <TableCell>{t('admin.clients.companyName')}</TableCell>
              <TableCell>{t('common.email')}</TableCell>
              <TableCell>{t('admin.clients.server')}</TableCell>
              <TableCell>{t('admin.clients.users')}</TableCell>
              <TableCell>{t('admin.clients.subscription')}</TableCell>
              <TableCell>{t('common.status')}</TableCell>
              <TableCell align="center">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <PeopleIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                  <Typography variant="body1" sx={{ color: '#9e9e9e' }}>
                    {t('admin.clients.noClients')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => (
                <TableRow
                  key={client.id}
                  sx={{
                    '&:hover': { backgroundColor: '#f5f7fa' },
                    transition: 'background-color 0.2s',
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {client.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{client.companyName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#546e7a' }}>
                      {client.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={client.serverName || `Server #${client.serverId}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${client.currentUsers || 0} / ${client.maxUsers}`}
                      size="small"
                      color={
                        (client.currentUsers || 0) >= client.maxUsers
                          ? 'error'
                          : (client.currentUsers || 0) >= client.maxUsers * 0.8
                          ? 'warning'
                          : 'default'
                      }
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#78909c', display: 'block' }}>
                        {new Date(client.subscriptionStart).toLocaleDateString()} -{' '}
                        {new Date(client.subscriptionEnd).toLocaleDateString()}
                      </Typography>
                      {new Date(client.subscriptionEnd) < new Date() && (
                        <Chip
                          label={t('common.expired')}
                          color="error"
                          size="small"
                          sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={client.isActive ? t('common.active') : t('common.inactive')}
                      color={client.isActive ? 'success' : 'error'}
                      size="small"
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('admin.clients.editClient')}>
                      <IconButton
                        onClick={() => openEditDialog(client)}
                        size="small"
                        sx={{
                          color: '#1a237e',
                          '&:hover': { backgroundColor: '#e8eaf6' },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={client.isActive ? t('admin.clients.deactivate') : t('admin.clients.activate')}>
                      <IconButton
                        onClick={() => handleToggleActive(client.id, client.isActive)}
                        size="small"
                        sx={{
                          color: client.isActive ? '#4caf50' : '#9e9e9e',
                          '&:hover': {
                            backgroundColor: client.isActive ? '#e8f5e9' : '#f5f5f5',
                          },
                        }}
                      >
                        {client.isActive ? (
                          <ToggleOnIcon fontSize="small" />
                        ) : (
                          <ToggleOffIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('admin.clients.deleteClient')}>
                      <IconButton
                        onClick={() => handleDelete(client.id)}
                        size="small"
                        sx={{
                          color: '#ef5350',
                          '&:hover': { backgroundColor: '#ffebee' },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setForm(initialForm);
          setEditingClient(null);
          setFormErrors({});
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
            color: '#ffffff',
            fontWeight: 700,
          }}
        >
          {editingClient ? t('admin.clients.editNewClient') : t('admin.clients.addNewClient')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          <TextField
            fullWidth
            label={t('admin.clients.fullName')}
            value={form.name}
            onChange={handleInputChange('name')}
            error={!!formErrors.name}
            helperText={formErrors.name}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('common.email')}
            type="email"
            value={form.email}
            onChange={handleInputChange('email')}
            error={!!formErrors.email}
            helperText={formErrors.email}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('common.password')}
            type="password"
            value={form.password}
            onChange={handleInputChange('password')}
            error={!!formErrors.password}
            helperText={formErrors.password || (editingClient ? t('admin.clients.passwordHint') : '')}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('common.phone')}
            value={form.phone}
            onChange={handleInputChange('phone')}
            error={!!formErrors.phone}
            helperText={formErrors.phone}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('admin.clients.companyName')}
            value={form.companyName}
            onChange={handleInputChange('companyName')}
            error={!!formErrors.companyName}
            helperText={formErrors.companyName}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }} error={!!formErrors.serverId}>
            <InputLabel>{t('admin.clients.server')}</InputLabel>
            <Select
              value={form.serverId}
              label={t('admin.clients.server')}
              onChange={handleSelectChange('serverId')}
            >
              {servers.map((server) => (
                <MenuItem key={server.id} value={String(server.id)}>
                  {server.name} ({server.ipAddress})
                </MenuItem>
              ))}
            </Select>
            {formErrors.serverId && (
              <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5 }}>
                {formErrors.serverId}
              </Typography>
            )}
          </FormControl>
          <TextField
            fullWidth
            label={t('admin.clients.maxUsers')}
            type="number"
            value={form.maxUsers}
            onChange={handleInputChange('maxUsers')}
            error={!!formErrors.maxUsers}
            helperText={
              formErrors.maxUsers ||
              (editingClient ? `${t('admin.clients.currentUsers')}: ${editingClient.currentUsers || 0}` : '')
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('admin.clients.subscriptionEnd')}
            type="date"
            value={form.subscriptionEnd}
            onChange={handleInputChange('subscriptionEnd')}
            error={!!formErrors.subscriptionEnd}
            helperText={formErrors.subscriptionEnd}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setForm(initialForm);
              setEditingClient(null);
              setFormErrors({});
            }}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #283593 0%, #1565c0 100%)',
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={24} sx={{ color: '#fff' }} />
            ) : editingClient ? (
              t('common.save')
            ) : (
              t('admin.clients.createClient')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Clients;
